/**
 * Firestore service for course comments
 */

import { firestore } from '../../config/firebase';
import { CourseComment } from '@e-learning/shared/src/store/types';
import { collection, doc, getDocs, getDoc, setDoc, deleteDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';

const COMMENTS_COLLECTION = 'comments';

/**
 * Get all comments for a course (sorted by newest first)
 */
export const getCourseComments = async (courseId: string): Promise<CourseComment[]> => {
  try {
    const commentsRef = collection(firestore, COMMENTS_COLLECTION);
    const q = query(
      commentsRef,
      where('courseId', '==', courseId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const comments: CourseComment[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      comments.push({
        id: doc.id,
        courseId: data.courseId,
        userId: data.userId,
        userEmail: data.userEmail || null,
        userName: data.userName || null,
        comment: data.comment,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || new Date().toISOString(),
      });
    });
    
    return comments;
  } catch (error: any) {
    console.error('Error fetching course comments:', error);
    throw new Error(error.message || 'Failed to fetch course comments');
  }
};

/**
 * Add a comment
 */
export const addComment = async (
  comment: Omit<CourseComment, 'id' | 'createdAt' | 'updatedAt'>
): Promise<CourseComment> => {
  try {
    const now = new Date();
    const commentRef = doc(collection(firestore, COMMENTS_COLLECTION));
    const commentData = {
      courseId: comment.courseId,
      userId: comment.userId,
      userEmail: comment.userEmail || null,
      userName: comment.userName || null,
      comment: comment.comment,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    
    await setDoc(commentRef, commentData);
    
    return {
      id: commentRef.id,
      ...comment,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };
  } catch (error: any) {
    console.error('Error adding comment:', error);
    throw new Error(error.message || 'Failed to add comment');
  }
};

/**
 * Update a comment
 */
export const updateComment = async (commentId: string, commentText: string): Promise<CourseComment> => {
  try {
    const commentRef = doc(firestore, COMMENTS_COLLECTION, commentId);
    const commentDoc = await getDoc(commentRef);
    
    if (!commentDoc.exists()) {
      throw new Error('Comment not found');
    }
    
    const data = commentDoc.data();
    const updatedData = {
      ...data,
      comment: commentText,
      updatedAt: Timestamp.now(),
    };
    
    await setDoc(commentRef, updatedData, { merge: true });
    
    return {
      id: commentId,
      courseId: data.courseId,
      userId: data.userId,
      userEmail: data.userEmail || null,
      userName: data.userName || null,
      comment: commentText,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  } catch (error: any) {
    console.error('Error updating comment:', error);
    throw new Error(error.message || 'Failed to update comment');
  }
};

/**
 * Delete a comment
 */
export const deleteComment = async (commentId: string): Promise<void> => {
  try {
    const commentRef = doc(firestore, COMMENTS_COLLECTION, commentId);
    await deleteDoc(commentRef);
  } catch (error: any) {
    console.error('Error deleting comment:', error);
    throw new Error(error.message || 'Failed to delete comment');
  }
};

